import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getOrg, getOrgPhoneNumbers, getOrgsForUser } from '../api/org';
import { Org, OrgPhoneNumber } from '../types/Org';

export const useOrgs = (inOrgContext = true) => {
  const { orgId } = useParams();
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Org | null>(null);
  const [currentOrgPhoneNumbers, setOrgPhoneNumbers] = useState<
    OrgPhoneNumber[]
  >([]);

  const fetchOrgs = useCallback(async () => {
    const fetchedOrgs = await getOrgsForUser();
    // Sort by created date ascending
    fetchedOrgs.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    setOrgs(fetchedOrgs);
    return fetchedOrgs;
  }, [setOrgs]);

  useEffect(() => {
    if (!inOrgContext) {
      fetchOrgs();
    }
  }, [inOrgContext, fetchOrgs]);

  const fetchOrgDetails = useCallback(
    async (orgId: number) => {
      try {
        const fetchedCurrentOrg = await getOrg(orgId);
        setCurrentOrg(fetchedCurrentOrg);
        return fetchedCurrentOrg;
      } catch (error) {
        console.error('Failed to fetch org details:', error);
        return null;
      }
    },
    [setCurrentOrg]
  );

  const fetchOrgPhoneNumbers = useCallback(
    async (orgId: number) => {
      try {
        const fetchedOrgPhoneNumbers = await getOrgPhoneNumbers(orgId);
        // Sort by created date ascending
        fetchedOrgPhoneNumbers.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        setOrgPhoneNumbers(fetchedOrgPhoneNumbers);
        return fetchedOrgPhoneNumbers;
      } catch (error) {
        console.error('Failed to fetch org phone numbers:', error);
        return [];
      }
    },
    [setOrgPhoneNumbers]
  );

  useEffect(() => {
    if (inOrgContext) {
      if (!orgId) return;
      const orgIdNum = parseInt(orgId);
      fetchOrgDetails(orgIdNum);
      fetchOrgPhoneNumbers(orgIdNum);
    }
  }, [orgId, inOrgContext, fetchOrgDetails, fetchOrgPhoneNumbers]);

  return {
    orgs,
    currentOrg,
    currentOrgPhoneNumbers,
    fetchOrgs,
    fetchOrgDetails,
    fetchOrgPhoneNumbers,
  };
};
